import { isPromptLayerSystemMessage } from './system-message-types'

/**
 * Keep the first prompt layer as the reusable prefix. Move only generated prompt
 * layers; metadata-bearing system instructions retain their system role.
 */
export function moveNonStableSystemLayersToLatestUser<T extends { role: string, content?: unknown, metadata?: unknown }>(messages: T[]): T[] {
  const promptLayers = messages.filter(isPromptLayerSystemMessage)
  if (promptLayers.length <= 1)
    return messages

  const movableLayers = new Set(promptLayers.slice(1))
  const dynamicText = promptLayers
    .slice(1)
    .map(message => extractText(message.content))
    .filter(Boolean)
    .join('\n\n---\n\n')

  if (!dynamicText)
    return messages

  const retainedMessages = messages.filter(message => !movableLayers.has(message))
  const latestUserIndex = findLastUserIndex(retainedMessages)
  if (latestUserIndex === -1)
    return messages

  return retainedMessages.map((message, index) => {
    if (index !== latestUserIndex)
      return message
    return {
      ...message,
      content: prependSystemContext(message.content, dynamicText),
    }
  }) as T[]
}

function findLastUserIndex(messages: Array<{ role: string }>): number {
  for (let index = messages.length - 1; index >= 0; index--) {
    if (messages[index].role === 'user')
      return index
  }
  return -1
}

function extractText(content: unknown): string {
  if (typeof content === 'string')
    return content
  if (!Array.isArray(content))
    return ''
  return content
    .map((part) => {
      if (!part || typeof part !== 'object')
        return ''
      const text = (part as { text?: unknown }).text
      return typeof text === 'string' ? text : ''
    })
    .join('')
}

function prependSystemContext(content: unknown, dynamicText: string): unknown {
  const prefix = `[系统上下文]\n${dynamicText}\n[/系统上下文]\n\n`
  if (typeof content === 'string')
    return prefix + content
  if (Array.isArray(content))
    return [{ type: 'text', text: prefix }, ...content]
  return prefix + String(content ?? '')
}
